import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { logger } from '@magnetar/utils'
import { Plus, Save, GripVertical, X } from 'lucide-react'
import { AdImageBatch, AdImageBatchItem } from '@/hooks/useAdImageBatches'
import { useAuth } from '@/hooks/useAuth'
import { adImageBatchService, AdImageBatchItemInput } from '@/services/ad_image_batch_service'
import { AdImageWithTag } from '@magnetar/admin-api-interfaces'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AddAdImageDialog from './AddAdImageDialog'

const getImageUrl = (fileUuid: string) => `${import.meta.env.VITE_API_URL}/api/files/download/${fileUuid}`

interface BatchItemsEditorProps {
  batch: AdImageBatch
  onSaved?: () => void
}

interface BatchItemRowProps {
  item: AdImageBatchItem
  onRemove: () => void
}

function BatchItemRow({ item, onRemove }: BatchItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.item_id.toString(),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
    >
      <div {...attributes} {...listeners} className="cursor-move">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <img
        src={getImageUrl(item.ad_image.file_uuid)}
        alt={`圖片 ${item.ad_image_id}`}
        className="w-12 h-12 object-cover rounded"
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">#{item.ad_image_id}</p>
        <p className="text-sm text-muted-foreground">
          {item.ad_image.play_duration_ms
            ? `${Math.round(item.ad_image.play_duration_ms / 1000)} 秒`
            : '未設定播放時長'}
        </p>
        {item.ad_image.tag_ad && (
          <p className="text-sm text-muted-foreground truncate">
            標記廣告：#{item.ad_image.tag_ad.advertisement_id} {item.ad_image.tag_ad.title || ''}
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="text-destructive hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default function BatchItemsEditor({ batch, onSaved }: BatchItemsEditorProps) {
  const { token } = useAuth()
  const [items, setItems] = useState<AdImageBatchItem[]>(
    batch.items.filter((item) => item.is_active),
  )
  const [isSaving, setIsSaving] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.item_id.toString() === active.id)
        const newIndex = items.findIndex((item) => item.item_id.toString() === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleAddImages = (selectedImages: AdImageWithTag[]) => {
    const newItems: AdImageBatchItem[] = selectedImages.map((image, index) => ({
      item_id: Date.now() + index, // 臨時 ID，儲存後會由後端重新產生
      batch_id: batch.batch_id,
      ad_image_id: image.ad_image_id,
      order: items.length + index,
      created_at: new Date().toISOString(),
      is_active: true,
      ad_image: {
        ad_image_id: image.ad_image_id,
        supplier_id: image.supplier_id,
        file_uuid: image.file_uuid,
        tag_ad_id: image.tag_ad_id,
        tag_ad: image.tag_ad,
        play_duration_ms: image.play_duration_ms,
        created_at:
          typeof image.created_at === 'string' ? image.created_at : image.created_at.toISOString(),
        updated_at:
          typeof image.updated_at === 'string' ? image.updated_at : image.updated_at.toISOString(),
        is_active: image.is_active,
      },
    }))
    setItems([...items, ...newItems])
  }

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    if (!token) return

    setIsSaving(true)
    try {
      const itemsInput: AdImageBatchItemInput[] = items.map((item, index) => ({
        ad_image_id: item.ad_image_id,
        order: index,
        is_active: true,
      }))
      const result = await adImageBatchService.replaceItems(batch.batch_id, itemsInput, token)
      if (result.success) {
        onSaved?.()
      }
    } catch (error) {
      logger.error('儲存項目失敗', error)
      toast.error('儲存項目失敗')
    } finally {
      setIsSaving(false)
    }
  }

  const existingImageIds = new Set(items.map((item) => item.ad_image_id))

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>圖片項目</CardTitle>
              <CardDescription>設定此批次包含的廣告圖片與播放順序</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              新增圖片
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {items.length > 0 ? (
            <ScrollArea className="h-[400px] pr-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((item) => item.item_id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <BatchItemRow
                        key={item.item_id}
                        item={item}
                        onRemove={() => handleRemoveItem(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </ScrollArea>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">尚未加入任何圖片</p>
              <Button variant="outline" className="mt-4" onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                新增圖片
              </Button>
            </div>
          )}

          <Separator />

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              儲存項目
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddAdImageDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        providerId={batch.provider_id ?? undefined}
        existingImageIds={existingImageIds}
        onAdd={handleAddImages}
      />
    </>
  )
}
